// src/components/PortalCorporativo.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import './PortalCorporativo.css';
import {
  FaNewspaper, FaBullhorn, FaDollarSign, FaBirthdayCake, FaSignature,
  FaAddressBook, FaFileAlt, FaLink, FaCalendarAlt, FaQuestionCircle,
  FaPoll, FaDoorOpen, FaTruck, FaCog, FaSyncAlt, FaCopy, FaCheck,
  FaArrowUp, FaArrowDown, FaThumbtack, FaExternalLinkAlt, FaWhatsapp,
  FaEnvelope, FaPhoneAlt, FaRegClock, FaChevronRight, FaPlus, FaSpinner,
} from 'react-icons/fa';

/* ============================================================================
 * Helpers
 * ========================================================================== */
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const primeiroNome = (nome = '') => String(nome).trim().split(/\s+/)[0] || '';
const iniciais = (nome = '') =>
  String(nome).trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');

const fmtData = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};
const fmtDataHora = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtMoeda = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/* ============================================================================
 * Casca de widget (título + ícone + corpo, com estados de loading/vazio)
 * ========================================================================== */
function Widget({ icon, titulo, subtitulo, className = '', actions, loading, vazio, vazioMsg = 'Nada por aqui ainda.', children }) {
  return (
    <section className={`pc-widget ${className}`}>
      <header className="pc-widget__head">
        <div className="pc-widget__title">
          <span className="pc-widget__icon">{icon}</span>
          <div>
            <h3>{titulo}</h3>
            {subtitulo && <p>{subtitulo}</p>}
          </div>
        </div>
        {actions && <div className="pc-widget__actions">{actions}</div>}
      </header>
      <div className="pc-widget__body">
        {loading ? (
          <div className="pc-widget__loading"><FaSpinner className="pc-spin" /> Carregando…</div>
        ) : vazio ? (
          <div className="pc-widget__empty">{vazioMsg}</div>
        ) : children}
      </div>
    </section>
  );
}

/* ============================================================================
 * 1. COMUNICADOS OFICIAIS
 * ========================================================================== */
function WidgetComunicados() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/comunicados')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaBullhorn />} titulo="Comunicados" subtitulo="Avisos oficiais da empresa"
      className="pc-widget--comunicados" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhum comunicado no momento."
    >
      <ul className="pc-comunicados">
        {itens.map((c) => (
          <li key={c.id} className={`pc-comunicado pc-prio-${c.prioridade}`}>
            {c.fixado ? <FaThumbtack className="pc-comunicado__pin" /> : null}
            <div className="pc-comunicado__body">
              <span className={`pc-badge pc-badge--${c.prioridade}`}>{c.prioridade}</span>
              <strong>{c.titulo}</strong>
              {c.corpo && <p>{c.corpo}</p>}
            </div>
          </li>
        ))}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 2. MURAL DE NOTÍCIAS
 * ========================================================================== */
function WidgetNoticias() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aberta, setAberta] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/noticias')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaNewspaper />} titulo="Mural de Notícias" subtitulo="Novidades e destaques"
      className="pc-widget--noticias pc-widget--wide" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhuma notícia publicada."
    >
      <div className="pc-noticias">
        {itens.map((n) => (
          <article key={n.id} className={`pc-noticia ${n.fixado ? 'is-fixada' : ''}`} onClick={() => setAberta(aberta === n.id ? null : n.id)}>
            {n.imagem_url && <div className="pc-noticia__img" style={{ backgroundImage: `url(${n.imagem_url})` }} />}
            <div className="pc-noticia__content">
              {n.categoria && <span className="pc-noticia__cat">{n.categoria}</span>}
              <h4>{n.titulo}</h4>
              {n.subtitulo && <p className="pc-noticia__sub">{n.subtitulo}</p>}
              <p className={`pc-noticia__corpo ${aberta === n.id ? 'is-open' : ''}`}>{n.corpo}</p>
              <span className="pc-noticia__data">{fmtData(n.publicado_em || n.criado_em)}</span>
            </div>
          </article>
        ))}
      </div>
    </Widget>
  );
}

/* ============================================================================
 * 3. COTAÇÃO DE MOEDAS
 * ========================================================================== */
function WidgetCotacao() {
  const [moedas, setMoedas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    api.get('/api/portal/cotacoes/moedas')
      .then(({ data }) => { setMoedas(data?.moedas || []); setStale(!!data?.stale); })
      .catch(() => setMoedas([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <Widget
      icon={<FaDollarSign />} titulo="Câmbio" subtitulo={stale ? 'Última cotação disponível' : 'Cotação do dia'}
      className="pc-widget--cotacao" loading={loading} vazio={!moedas.length}
      vazioMsg="Cotação indisponível."
      actions={<button className="pc-icon-btn" onClick={carregar} title="Atualizar"><FaSyncAlt /></button>}
    >
      <ul className="pc-cotacao">
        {moedas.map((m) => {
          const subiu = Number(m.variacao) >= 0;
          return (
            <li key={m.par} className="pc-cotacao__item">
              <div className="pc-cotacao__par">
                <strong>{m.par.replace('-BRL', '')}</strong>
                <span>{m.nome}</span>
              </div>
              <div className="pc-cotacao__valor">
                <strong>{fmtMoeda(m.venda)}</strong>
                <span className={subiu ? 'is-up' : 'is-down'}>
                  {subiu ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(Number(m.variacao)).toFixed(2)}%
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 4. ANIVERSARIANTES
 * ========================================================================== */
function WidgetAniversariantes() {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    api.get(`/api/portal/aniversariantes?mes=${mes}`)
      .then(({ data }) => { if (vivo) setItens(data?.aniversariantes || []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [mes]);

  return (
    <Widget
      icon={<FaBirthdayCake />} titulo="Aniversariantes" subtitulo={MESES[mes - 1]}
      className="pc-widget--aniversarios" loading={loading} vazio={!itens.length}
      vazioMsg="Ninguém faz aniversário neste mês."
      actions={
        <select className="pc-select-sm" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
          {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      }
    >
      <ul className="pc-aniversarios">
        {itens.map((a) => (
          <li key={a.id} className="pc-aniversario">
            <div className="pc-aniversario__dia">{String(a.dia).padStart(2, '0')}</div>
            <div className="pc-avatar">
              {a.foto_perfil ? <img src={a.foto_perfil} alt={a.nome} /> : iniciais(a.nome)}
            </div>
            <div className="pc-aniversario__info">
              <strong>{a.nome}</strong>
              <span>{a.cargo || '—'}</span>
            </div>
            <FaBirthdayCake className="pc-aniversario__cake" />
          </li>
        ))}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 5. GERADOR DE ASSINATURA DE E-MAIL
 * ========================================================================== */
function montarAssinaturaHtml(cfg, campos) {
  const primaria = cfg?.cor_primaria || '#0d7377';
  const secundaria = cfg?.cor_secundaria || '#14213d';
  const linhaContato = (label, valor) => valor
    ? `<div style="font-size:12px;color:#555;margin-top:2px;">${label} ${valor}</div>` : '';
  return `
<table cellpadding="0" cellspacing="0" style="font-family:Arial,Helvetica,sans-serif;color:#14213d;">
  <tr>
    ${cfg?.logo_url ? `<td style="padding-right:16px;border-right:3px solid ${primaria};vertical-align:top;">
      <img src="${cfg.logo_url}" alt="logo" style="max-width:96px;height:auto;display:block;" /></td>` : ''}
    <td style="padding-left:16px;vertical-align:top;">
      <div style="font-size:16px;font-weight:bold;color:${secundaria};">${campos.nome || ''}</div>
      <div style="font-size:13px;color:${primaria};font-weight:bold;">${campos.cargo || ''}</div>
      <div style="font-size:13px;color:#333;margin-top:4px;">${cfg?.empresa_nome || ''}</div>
      ${linhaContato('✉', campos.email)}
      ${linhaContato('☎', campos.telefone)}
      ${cfg?.empresa_site ? `<div style="font-size:12px;margin-top:2px;"><a href="${cfg.empresa_site}" style="color:${primaria};text-decoration:none;">${cfg.empresa_site.replace(/^https?:\/\//, '')}</a></div>` : ''}
      ${cfg?.empresa_endereco ? `<div style="font-size:11px;color:#888;margin-top:4px;">${cfg.empresa_endereco}</div>` : ''}
      ${cfg?.disclaimer ? `<div style="font-size:10px;color:#aaa;margin-top:8px;max-width:420px;">${cfg.disclaimer}</div>` : ''}
    </td>
  </tr>
</table>`.trim();
}

function WidgetAssinatura({ user }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiado, setCopiado] = useState(false);
  const previewRef = useRef(null);
  const [campos, setCampos] = useState({
    nome: user?.nome || '',
    cargo: user?.cargo || '',
    email: user?.email || '',
    telefone: user?.telefone || '',
  });

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/assinatura/config')
      .then(({ data }) => { if (vivo) setCfg(data || {}); })
      .catch(() => { if (vivo) setCfg({}); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const html = useMemo(() => montarAssinaturaHtml(cfg, campos), [cfg, campos]);
  const setCampo = (k) => (e) => setCampos((p) => ({ ...p, [k]: e.target.value }));

  const copiar = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const blobHtml = new Blob([html], { type: 'text/html' });
        const blobText = new Blob([previewRef.current?.innerText || ''], { type: 'text/plain' });
        await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopiado(true);
      toast.success('Assinatura copiada! Cole no seu cliente de e-mail.');
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.error('Não foi possível copiar. Selecione a prévia e copie manualmente.');
    }
  };

  return (
    <Widget
      icon={<FaSignature />} titulo="Assinatura de E-mail" subtitulo="Gere e copie a sua assinatura"
      className="pc-widget--assinatura pc-widget--wide" loading={loading}
    >
      <div className="pc-assinatura">
        <div className="pc-assinatura__form">
          <label>Nome<input value={campos.nome} onChange={setCampo('nome')} placeholder="Seu nome" /></label>
          <label>Cargo<input value={campos.cargo} onChange={setCampo('cargo')} placeholder="Seu cargo" /></label>
          <label>E-mail<input value={campos.email} onChange={setCampo('email')} placeholder="voce@empresa.com" /></label>
          <label>Telefone<input value={campos.telefone} onChange={setCampo('telefone')} placeholder="(00) 00000-0000" /></label>
          <button className={`pc-btn-primary ${copiado ? 'is-ok' : ''}`} onClick={copiar}>
            {copiado ? <><FaCheck /> Copiado</> : <><FaCopy /> Copiar assinatura</>}
          </button>
        </div>
        <div className="pc-assinatura__preview">
          <span className="pc-assinatura__preview-label">Prévia</span>
          <div ref={previewRef} className="pc-assinatura__render" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
    </Widget>
  );
}

/* ============================================================================
 * 6. RAMAIS / CONTATOS
 * ========================================================================== */
function WidgetContatos() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/contatos')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return itens;
    return itens.filter((c) =>
      [c.nome, c.setor, c.cargo, c.ramal].some((v) => String(v || '').toLowerCase().includes(t)));
  }, [itens, busca]);

  return (
    <Widget
      icon={<FaAddressBook />} titulo="Ramais & Contatos" subtitulo="Equipe interna"
      className="pc-widget--contatos" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhum contato cadastrado."
      actions={<input className="pc-search-sm" placeholder="Buscar…" value={busca} onChange={(e) => setBusca(e.target.value)} />}
    >
      <ul className="pc-contatos">
        {filtrados.map((c) => (
          <li key={c.id} className="pc-contato">
            <div className="pc-avatar">{c.foto_url ? <img src={c.foto_url} alt={c.nome} /> : iniciais(c.nome)}</div>
            <div className="pc-contato__info">
              <strong>{c.nome}</strong>
              <span>{[c.cargo, c.setor].filter(Boolean).join(' • ')}</span>
            </div>
            <div className="pc-contato__acoes">
              {c.ramal && <span className="pc-contato__ramal"><FaPhoneAlt /> {c.ramal}</span>}
              {c.whatsapp && <a href={`https://wa.me/55${String(c.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" title="WhatsApp"><FaWhatsapp /></a>}
              {c.email && <a href={`mailto:${c.email}`} title="E-mail"><FaEnvelope /></a>}
            </div>
          </li>
        ))}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 7. DOCUMENTOS E POLÍTICAS
 * ========================================================================== */
function WidgetDocumentos() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/documentos')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaFileAlt />} titulo="Documentos & Políticas" subtitulo="Arquivos oficiais"
      className="pc-widget--documentos" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhum documento disponível."
    >
      <ul className="pc-documentos">
        {itens.map((d) => (
          <li key={d.id}>
            <a href={d.drive_url} target="_blank" rel="noreferrer" className="pc-documento">
              <span className="pc-documento__tipo">{(d.tipo || 'doc').toUpperCase()}</span>
              <div className="pc-documento__info">
                <strong>{d.titulo}</strong>
                {d.categoria && <span>{d.categoria}</span>}
              </div>
              {d.obrigatorio_leitura ? <span className="pc-tag-obrig">Leitura obrigatória</span> : null}
              <FaExternalLinkAlt className="pc-documento__ext" />
            </a>
          </li>
        ))}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 8. LINKS RÁPIDOS
 * ========================================================================== */
function WidgetLinks() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/links')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaLink />} titulo="Links Rápidos" subtitulo="Sistemas e atalhos"
      className="pc-widget--links" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhum link cadastrado."
    >
      <div className="pc-links">
        {itens.map((l) => (
          <a key={l.id} href={l.url} target={l.abre_nova_aba ? '_blank' : '_self'} rel="noreferrer"
            className="pc-link-card" style={{ '--pc-link-cor': l.cor || 'var(--pc-teal)' }}>
            <span className="pc-link-card__dot" />
            <strong>{l.label}</strong>
            {l.descricao && <span>{l.descricao}</span>}
          </a>
        ))}
      </div>
    </Widget>
  );
}

/* ============================================================================
 * 9. CALENDÁRIO DE EVENTOS
 * ========================================================================== */
function WidgetEventos() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/eventos')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const proximos = useMemo(
    () => itens.filter((e) => !e.data_fim || new Date(e.data_fim) >= new Date(new Date().toDateString())),
    [itens],
  );

  return (
    <Widget
      icon={<FaCalendarAlt />} titulo="Agenda & Eventos" subtitulo="Próximos acontecimentos"
      className="pc-widget--eventos" loading={loading} vazio={!proximos.length}
      vazioMsg="Nenhum evento agendado."
    >
      <ul className="pc-eventos">
        {proximos.map((e) => {
          const d = new Date(e.data_inicio);
          return (
            <li key={e.id} className={`pc-evento pc-evento--${e.tipo}`}>
              <div className="pc-evento__data">
                <span className="pc-evento__dia">{String(d.getDate()).padStart(2, '0')}</span>
                <span className="pc-evento__mes">{MESES[d.getMonth()]?.slice(0, 3)}</span>
              </div>
              <div className="pc-evento__info">
                <strong>{e.titulo}</strong>
                <span>{[e.local, e.dia_inteiro ? 'Dia inteiro' : fmtDataHora(e.data_inicio)].filter(Boolean).join(' • ')}</span>
              </div>
              <span className={`pc-badge pc-badge--evento`}>{e.tipo}</span>
            </li>
          );
        })}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 10. FAQ
 * ========================================================================== */
function WidgetFaq() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aberta, setAberta] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/faq')
      .then(({ data }) => { if (vivo) setItens(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setItens([]); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaQuestionCircle />} titulo="Central de Ajuda" subtitulo="Perguntas frequentes"
      className="pc-widget--faq" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhuma pergunta cadastrada."
    >
      <ul className="pc-faq">
        {itens.map((f) => (
          <li key={f.id} className={`pc-faq__item ${aberta === f.id ? 'is-open' : ''}`}>
            <button className="pc-faq__q" onClick={() => setAberta(aberta === f.id ? null : f.id)}>
              <span>{f.pergunta}</span>
              <FaChevronRight className="pc-faq__chevron" />
            </button>
            {aberta === f.id && <div className="pc-faq__a">{f.resposta}</div>}
          </li>
        ))}
      </ul>
    </Widget>
  );
}

/* ============================================================================
 * 11. ENQUETES
 * ========================================================================== */
function WidgetEnquetes() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecao, setSelecao] = useState({}); // { [enqueteId]: Set(opcaoId) }
  const [enviando, setEnviando] = useState(null);

  const carregar = useCallback(() => {
    api.get('/api/portal/enquetes')
      .then(({ data }) => setItens(Array.isArray(data) ? data : []))
      .catch(() => setItens([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const toggleOpcao = (enq, opcaoId) => {
    setSelecao((prev) => {
      const atual = new Set(prev[enq.id] || []);
      if (enq.tipo === 'unica') { atual.clear(); atual.add(opcaoId); }
      else if (atual.has(opcaoId)) atual.delete(opcaoId);
      else atual.add(opcaoId);
      return { ...prev, [enq.id]: atual };
    });
  };

  const votar = async (enq) => {
    const ids = Array.from(selecao[enq.id] || []);
    if (!ids.length) return toast.warn('Selecione uma opção.');
    setEnviando(enq.id);
    try {
      await api.post(`/api/portal/enquetes/${enq.id}/votar`, { opcaoIds: ids });
      toast.success('Voto registrado!');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao votar.');
    } finally {
      setEnviando(null);
    }
  };

  const totalVotos = (enq) => (enq.opcoes || []).reduce((s, o) => s + Number(o.votos_count || 0), 0);

  return (
    <Widget
      icon={<FaPoll />} titulo="Enquetes" subtitulo="Sua opinião importa"
      className="pc-widget--enquetes" loading={loading} vazio={!itens.length}
      vazioMsg="Nenhuma enquete aberta."
    >
      <div className="pc-enquetes">
        {itens.map((enq) => {
          const total = totalVotos(enq);
          const jaVotou = enq.ja_votou;
          return (
            <div key={enq.id} className="pc-enquete">
              <strong className="pc-enquete__titulo">{enq.titulo}</strong>
              {enq.descricao && <p className="pc-enquete__desc">{enq.descricao}</p>}
              <ul className="pc-enquete__opcoes">
                {(enq.opcoes || []).map((o) => {
                  const pct = total > 0 ? Math.round((Number(o.votos_count || 0) / total) * 100) : 0;
                  const marcada = (selecao[enq.id] || new Set()).has(o.id);
                  const minhaEscolha = (enq.meus_votos || []).includes(o.id);
                  return (
                    <li key={o.id}>
                      {jaVotou ? (
                        <div className={`pc-enquete__resultado ${minhaEscolha ? 'is-minha' : ''}`}>
                          <div className="pc-enquete__barra" style={{ width: `${pct}%` }} />
                          <span className="pc-enquete__txt">{o.texto}</span>
                          <span className="pc-enquete__pct">{pct}%</span>
                        </div>
                      ) : (
                        <button className={`pc-enquete__opcao ${marcada ? 'is-sel' : ''}`} onClick={() => toggleOpcao(enq, o.id)}>
                          <span className="pc-enquete__check">{marcada ? <FaCheck /> : null}</span>
                          {o.texto}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {!jaVotou && (
                <button className="pc-btn-primary pc-btn-sm" disabled={enviando === enq.id} onClick={() => votar(enq)}>
                  {enviando === enq.id ? <><FaSpinner className="pc-spin" /> Enviando…</> : 'Votar'}
                </button>
              )}
              {jaVotou && <span className="pc-enquete__total">{total} voto(s) • você já participou</span>}
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

/* ============================================================================
 * 12. RESERVA DE RECURSOS
 * ========================================================================== */
function WidgetReservas({ user }) {
  const [recursos, setRecursos] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ recurso_id: '', titulo: '', inicio: '', fim: '' });
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [rRec, rRes] = await Promise.all([
        api.get('/api/portal/recursos'),
        api.get('/api/portal/reservas'),
      ]);
      setRecursos(Array.isArray(rRec.data) ? rRec.data : []);
      setReservas(Array.isArray(rRes.data) ? rRes.data : []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const setCampo = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reservar = async () => {
    if (!form.recurso_id || !form.inicio || !form.fim) return toast.warn('Preencha recurso, início e fim.');
    setEnviando(true);
    try {
      const { data } = await api.post('/api/portal/reservas', form);
      toast.success(data?.message || 'Reserva registrada!');
      setForm({ recurso_id: '', titulo: '', inicio: '', fim: '' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao reservar.');
    } finally {
      setEnviando(false);
    }
  };

  const cancelar = async (id) => {
    try {
      await api.put(`/api/portal/reservas/${id}/status`, { status: 'cancelada' });
      toast.info('Reserva cancelada.');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao cancelar.');
    }
  };

  return (
    <Widget
      icon={<FaDoorOpen />} titulo="Reserva de Recursos" subtitulo="Salas, veículos e equipamentos"
      className="pc-widget--reservas pc-widget--wide" loading={loading} vazio={!recursos.length}
      vazioMsg="Nenhum recurso disponível para reserva."
    >
      <div className="pc-reservas">
        <div className="pc-reservas__form">
          <select value={form.recurso_id} onChange={setCampo('recurso_id')}>
            <option value="">Selecione um recurso…</option>
            {recursos.map((r) => <option key={r.id} value={r.id}>{r.nome} ({r.tipo})</option>)}
          </select>
          <input placeholder="Título (opcional)" value={form.titulo} onChange={setCampo('titulo')} />
          <label className="pc-reservas__dt">Início<input type="datetime-local" value={form.inicio} onChange={setCampo('inicio')} /></label>
          <label className="pc-reservas__dt">Fim<input type="datetime-local" value={form.fim} onChange={setCampo('fim')} /></label>
          <button className="pc-btn-primary" disabled={enviando} onClick={reservar}>
            {enviando ? <><FaSpinner className="pc-spin" /> …</> : <><FaPlus /> Reservar</>}
          </button>
        </div>

        <div className="pc-reservas__lista">
          <span className="pc-reservas__lista-label"><FaRegClock /> Próximas reservas</span>
          {reservas.length === 0 ? (
            <p className="pc-reservas__vazio">Sem reservas futuras.</p>
          ) : (
            <ul>
              {reservas.map((r) => (
                <li key={r.id} className={`pc-reserva pc-reserva--${r.status}`}>
                  <div className="pc-reserva__info">
                    <strong>{r.recurso_nome}</strong>
                    <span>{fmtDataHora(r.inicio)} → {fmtDataHora(r.fim)}</span>
                    <span className="pc-reserva__quem">{r.usuario_nome}</span>
                  </div>
                  <span className={`pc-badge pc-badge--${r.status}`}>{r.status}</span>
                  {Number(r.usuario_id) === Number(user?.id) && r.status !== 'cancelada' && (
                    <button className="pc-link-danger" onClick={() => cancelar(r.id)}>cancelar</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Widget>
  );
}

/* ============================================================================
 * 13. INDICADORES DE FROTA
 * ========================================================================== */
function WidgetIndicadores() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/indicadores/frota')
      .then(({ data }) => { if (vivo) setDados(data || null); })
      .catch(() => { if (vivo) setDados(null); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  return (
    <Widget
      icon={<FaTruck />} titulo="Indicadores de Frota" subtitulo="Resumo operacional"
      className="pc-widget--indicadores" loading={loading} vazio={!dados}
      vazioMsg="Sem dados operacionais."
    >
      <div className="pc-kpis">
        <div className="pc-kpi"><span className="pc-kpi__num">{dados?.os_em_aberto ?? 0}</span><span className="pc-kpi__lbl">OS em aberto</span></div>
        <div className="pc-kpi"><span className="pc-kpi__num">{dados?.os_concluidas ?? 0}</span><span className="pc-kpi__lbl">OS concluídas</span></div>
        <div className="pc-kpi"><span className="pc-kpi__num">{dados?.veiculos_total ?? '—'}</span><span className="pc-kpi__lbl">Veículos</span></div>
        <div className="pc-kpi"><span className="pc-kpi__num">{dados?.os_total ?? 0}</span><span className="pc-kpi__lbl">OS totais</span></div>
      </div>
    </Widget>
  );
}

/* ============================================================================
 * Registro de widgets + ordem default
 * ========================================================================== */
const WIDGETS = {
  comunicados: WidgetComunicados,
  noticias: WidgetNoticias,
  cotacao: WidgetCotacao,
  aniversariantes: WidgetAniversariantes,
  assinatura: WidgetAssinatura,
  contatos: WidgetContatos,
  documentos: WidgetDocumentos,
  links: WidgetLinks,
  eventos: WidgetEventos,
  faq: WidgetFaq,
  enquetes: WidgetEnquetes,
  reservas: WidgetReservas,
  indicadores: WidgetIndicadores,
};
const ORDEM_DEFAULT = [
  'comunicados', 'indicadores', 'noticias', 'cotacao', 'aniversariantes',
  'assinatura', 'enquetes', 'contatos', 'links', 'documentos', 'eventos', 'reservas', 'faq',
];

/* ============================================================================
 * COMPONENTE PRINCIPAL
 * ========================================================================== */
export default function PortalCorporativo({ user }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [contexto, setContexto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    Promise.allSettled([
      api.get('/api/portal/contexto'),
      api.get('/api/portal/config'),
    ]).then(([ctx, cfg]) => {
      if (!vivo) return;
      if (ctx.status === 'fulfilled') setContexto(ctx.value.data);
      if (cfg.status === 'fulfilled') setConfig(cfg.value.data);
    }).finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  // Resolve a ordem/habilitação a partir do layout_json (se houver)
  const widgetsOrdenados = useMemo(() => {
    let layout = config?.layout_json;
    if (typeof layout === 'string') { try { layout = JSON.parse(layout); } catch { layout = null; } }

    if (Array.isArray(layout) && layout.length) {
      return layout
        .filter((w) => w && w.enabled !== false && WIDGETS[w.key])
        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
        .map((w) => w.key);
    }
    return ORDEM_DEFAULT;
  }, [config]);

  if (loading) {
    return (
      <div className="pc-loading-full"><FaSpinner className="pc-spin" /> Carregando o portal…</div>
    );
  }

  const saudacao = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  return (
    <div className="pc-portal">
      {/* HERO / BANNER */}
      <header
        className="pc-hero"
        style={config?.banner_imagem_url ? { backgroundImage: `linear-gradient(120deg, rgba(13,115,119,.92), rgba(20,33,61,.82)), url(${config.banner_imagem_url})` } : undefined}
      >
        <div className="pc-hero__content">
          <span className="pc-hero__eyebrow">Portal Corporativo</span>
          <h1>{config?.banner_titulo || `${saudacao}, ${primeiroNome(user?.nome) || 'colaborador'}!`}</h1>
          <p>{config?.banner_subtitulo || 'Tudo o que você precisa da empresa, em um só lugar.'}</p>
        </div>
        {contexto?.podeConfigurar && (
          <button className="pc-hero__config" onClick={() => navigate('/portal/config')}>
            <FaCog /> Configurar portal
          </button>
        )}
      </header>

      {/* GRID DE WIDGETS */}
      <main className="pc-grid">
        {widgetsOrdenados.map((key) => {
          const Comp = WIDGETS[key];
          if (!Comp) return null;
          return <Comp key={key} user={user} />;
        })}
      </main>

      <footer className="pc-footer">
        <span>BLB System • Portal Corporativo</span>
      </footer>
    </div>
  );
}